import { APP_VERSION, can } from '@esportsbudget/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Building2,
  CalendarDays,
  ChevronsUpDown,
  CreditCard,
  FileBarChart,
  FolderOpen,
  Handshake,
  History,
  LayoutGrid,
  LifeBuoy,
  LogOut,
  Menu,
  PlayCircle,
  Plus,
  Repeat,
  Settings,
  Shield,
  ShieldCheck,
  TrendingUp,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { BillingHeaderWidget } from '@/components/billing/BillingHeaderWidget';
import { LogoMark } from '@/components/brand/Logo';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { LocaleToggle, ThemeToggle } from '@/components/layout/Toggles';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { useFormatters } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuth } from '@/providers/AuthProvider';

interface NavEntry {
  to: string;
  labelKey: string;
  icon: typeof LayoutGrid;
  /** Источник счётчика непрочитанного рядом с названием. */
  badge?: 'support';
}

const MAIN_NAV: NavEntry[] = [
  { to: '/', labelKey: 'nav:dashboard', icon: LayoutGrid },
  { to: '/people', labelKey: 'nav:people', icon: Users },
  { to: '/payouts', labelKey: 'nav:payouts', icon: TrendingUp },
];

const FINANCE_NAV: NavEntry[] = [
  { to: '/wallets', labelKey: 'nav:wallets', icon: Wallet },
  { to: '/transactions', labelKey: 'nav:transactions', icon: CreditCard },
  { to: '/income', labelKey: 'nav:income', icon: Handshake },
  { to: '/recurring', labelKey: 'nav:recurring', icon: Repeat },
  { to: '/calendar', labelKey: 'nav:calendar', icon: CalendarDays },
];

const OTHER_NAV: NavEntry[] = [
  { to: '/documents', labelKey: 'nav:documents', icon: FolderOpen },
  { to: '/reports', labelKey: 'nav:reports', icon: FileBarChart },
  { to: '/support', labelKey: 'nav:support', icon: LifeBuoy, badge: 'support' },
  { to: '/settings', labelKey: 'nav:settings', icon: Settings },
];

/**
 * Нижняя панель на телефоне.
 *
 * Пять разделов, в которые заходят каждый день. Остальное — за кнопкой меню:
 * шесть иконок в ряд на узком экране уже не попадают под палец.
 */
const BOTTOM_NAV: NavEntry[] = [
  { to: '/', labelKey: 'nav:dashboard', icon: LayoutGrid },
  { to: '/payouts', labelKey: 'nav:payouts', icon: TrendingUp },
  { to: '/transactions', labelKey: 'nav:transactions', icon: CreditCard },
  { to: '/people', labelKey: 'nav:people', icon: Users },
  { to: '/calendar', labelKey: 'nav:calendar', icon: CalendarDays },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['nav', 'common', 'workspace', 'auth']);
  const { me, workspace, workspaces, selectWorkspace, logout } = useAuth();
  const navigate = useNavigate();
  const format = useFormatters();
  const location = useLocation();

  // Счётчик обращений с ответом поддержки. Раз в полминуты: ответ приходит
  // не мгновенно, но минута ожидания уже читается как «не работает».
  // Перезапрос при возврате на вкладку — чтобы значок не показывал вчерашнее.
  const supportUnread = useQuery({
    queryKey: ['support-unread'],
    queryFn: () => api.get<{ count: number }>('/support/unread-count'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const wsRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Закрываем выпадающие меню по клику вне и по Escape.
  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (wsRef.current && !wsRef.current.contains(event.target as Node)) setWsMenuOpen(false);
      if (userRef.current && !userRef.current.contains(event.target as Node)) setUserMenuOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setWsMenuOpen(false);
        setUserMenuOpen(false);
        setMobileOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const initials = (workspace?.name ?? '??')
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

  function renderNav(entries: NavEntry[]) {
    return entries.map((entry) => {
      const label = t(entry.labelKey);

      return (
        <NavLink
          key={entry.to}
          to={entry.to}
          end={entry.to === '/'}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2.5 rounded-lg px-2 py-[7px] text-[13.5px] transition-colors duration-150',
              isActive
                ? 'bg-accent-soft font-semibold text-accent'
                : 'text-muted hover:bg-surface-2 hover:text-text',
            )
          }
        >
          <entry.icon className="h-[15px] w-[15px] shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
          {entry.badge === 'support' && (supportUnread.data?.count ?? 0) > 0 && (
            <span className="ml-auto rounded-full bg-accent px-1.5 py-px text-[10px] font-semibold text-accent-text">
              {supportUnread.data!.count}
            </span>
          )}
        </NavLink>
      );
    });
  }

  const sidebar = (
    <div className="flex h-full flex-col">
      {/* Бренд-марка — на десктопе сайдбар открыт всегда, и это единственное
          место в основном приложении, где логотип виден постоянно (в шапке
          он только на мобильном, см. ниже). */}
      <div className="flex items-center gap-2 px-4 pt-4">
        <LogoMark className="h-6 w-6" decorative />
        <span className="text-[13.5px] font-semibold tracking-tight">{t('common:appName')}</span>
      </div>

      {/* Переключатель воркспейсов */}
      <div className="relative px-3 pt-3" ref={wsRef}>
        <button
          type="button"
          onClick={() => setWsMenuOpen((open) => !open)}
          aria-expanded={wsMenuOpen}
          className="flex w-full items-center gap-2.5 rounded-[10px] border border-border p-2 text-left transition-colors duration-150 hover:bg-surface-2"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent text-[11px] font-bold text-accent-text">
            {initials || '—'}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold leading-tight">
              {workspace?.name ?? '—'}
            </span>
            <span className="block truncate text-[11px] text-subtle">
              {workspace ? t(`workspace:role.${workspace.role}`) : ''}
            </span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden />
        </button>

        {wsMenuOpen && (
          <div className="absolute left-3 right-3 top-full z-30 mt-1 overflow-hidden rounded-[10px] border border-border bg-surface shadow-float animate-in">
            <div className="max-h-64 overflow-y-auto p-1">
              {workspaces.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    selectWorkspace(item.id);
                    setWsMenuOpen(false);
                    navigate('/');
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] transition-colors duration-150',
                    item.id === workspace?.id
                      ? 'bg-accent-soft font-semibold text-accent'
                      : 'text-text hover:bg-surface-2',
                  )}
                >
                  <Building2 className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  <span className="truncate">{item.name}</span>
                </button>
              ))}
            </div>
            <div className="border-t border-border p-1">
              <Link
                to="/workspace/new"
                onClick={() => setWsMenuOpen(false)}
                className="flex items-center gap-2 rounded-lg px-2 py-2 text-[13px] text-muted transition-colors duration-150 hover:bg-surface-2 hover:text-text"
              >
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                {t('workspace:createNew')}
              </Link>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 pt-4">
        <div className="flex flex-col gap-0.5">{renderNav(MAIN_NAV)}</div>

        <p className="px-2 pb-1.5 pt-4 text-[10.5px] font-semibold uppercase tracking-wider text-subtle">
          {t('nav:finance')}
        </p>
        <div className="flex flex-col gap-0.5">{renderNav(FINANCE_NAV)}</div>

        <p className="px-2 pb-1.5 pt-4 text-[10.5px] font-semibold uppercase tracking-wider text-subtle">
          {t('common:more')}
        </p>
        <div className="flex flex-col gap-0.5">{renderNav(OTHER_NAV)}</div>

        {me?.user.role === 'SUPERADMIN' && (
          <Link
            to="/admin"
            className="mt-4 flex items-center gap-2.5 rounded-lg border border-border px-2 py-[7px] text-[13.5px] text-muted transition-colors duration-150 hover:bg-surface-2 hover:text-text"
          >
            <Shield className="h-[15px] w-[15px] shrink-0" aria-hidden />
            {t('nav:admin')}
          </Link>
        )}
      </nav>

      {/*
        Версия внизу — обычная ссылка на журнал изменений. Человек, который
        замечает, что программа изменилась, первым делом ищет, что именно
        поменялось; пусть находит это в одном нажатии, а не в поддержке.
      */}
      <Link
        to="/devlog"
        className="mx-3 mb-3 mt-1 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11.5px] text-subtle transition-colors duration-150 hover:bg-surface-2 hover:text-muted"
        title={t('common:devlog.title')}
      >
        <History className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="font-mono">v{APP_VERSION}</span>
      </Link>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Сайдбар на десктопе */}
      <aside className="hidden w-[212px] shrink-0 border-r border-border bg-surface lg:block">
        <div className="sticky top-0 h-screen">{sidebar}</div>
      </aside>

      {/* Сайдбар на мобильном */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <div className="absolute left-0 top-0 h-full w-[260px] border-r border-border bg-surface animate-in">
            <div className="flex items-center justify-end p-2">
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} aria-label={t('common:close')}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {sidebar}
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-border bg-surface/95 px-4 py-2.5 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label={t('common:more')}
          >
            <Menu className="h-[18px] w-[18px]" />
          </Button>

          <div className="flex items-center gap-2 lg:hidden">
            <LogoMark className="h-7 w-7" decorative />
            <span className="text-[14px] font-semibold">{t('common:appName')}</span>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <BillingHeaderWidget />

            <NotificationBell />

            <LocaleToggle />
            <ThemeToggle />

            <div className="relative ml-1" ref={userRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((open) => !open)}
                aria-expanded={userMenuOpen}
                className="flex h-9 items-center gap-2 rounded-[10px] pl-1 pr-2 transition-colors duration-150 hover:bg-surface-2"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-3 text-[11px] font-semibold text-muted">
                  {(me?.user.displayName ?? '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden max-w-[140px] truncate text-[13px] font-medium sm:block">
                  {me?.user.displayName}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-[10px] border border-border bg-surface shadow-float animate-in">
                  <div className="border-b border-border px-3 py-2.5">
                    <p className="truncate text-[13px] font-semibold">{me?.user.displayName}</p>
                    <p className="truncate text-[12px] text-subtle">@{me?.user.username}</p>
                    {me?.plan.plan && (
                      <p className="mt-1.5 inline-flex rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">
                        {me.plan.plan.name}
                      </p>
                    )}
                  </div>
                  <div className="p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        void logout();
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] text-muted transition-colors duration-150 hover:bg-surface-2 hover:text-text"
                    >
                      <LogOut className="h-4 w-4" aria-hidden />
                      {t('common:logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Демо (F-25): человек должен знать, что данные исчезнут, до того,
            как начнёт вносить в них реальные суммы. */}
        {me?.user.isDemo && (
          <div className="mx-auto w-full max-w-[1180px] px-4 pt-4 sm:px-6">
            <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-warning/40 bg-warning/10 px-3.5 py-2.5">
              <PlayCircle className="h-4 w-4 shrink-0 text-warning" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium">{t('auth:demoBannerTitle')}</p>
                <p className="text-[12.5px] text-muted">
                  {t('auth:demoBannerBody', {
                    when: me.demoExpiresAt ? format.dateTime(me.demoExpiresAt) : '',
                  })}
                </p>
              </div>
              <Link
                to="/register"
                className="shrink-0 rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-medium text-accent-text"
              >
                {t('auth:demoRegister')}
              </Link>
            </div>
          </div>
        )}

        {/* Администратор платформы часто заходит как обычный пользователь и
            не сразу вспоминает адрес панели — показываем прямую ссылку. */}
        {me?.user.role === 'SUPERADMIN' && (
          <div className="mx-auto w-full max-w-[1180px] px-4 pt-4 sm:px-6">
            <Link
              to="/admin"
              className="flex items-center gap-2 rounded-[10px] border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-[13px] text-text transition-colors hover:bg-danger/15"
            >
              <ShieldCheck className="h-4 w-4 text-danger" aria-hidden />
              {t('nav:adminBanner')}
            </Link>
          </div>
        )}

        {/* Снизу оставляем место под панель: иначе она накрывает последнюю строку списка. */}
        <main className="mx-auto w-full max-w-[1180px] flex-1 px-4 pb-24 pt-6 sm:px-6 lg:pb-6">
          {children}
        </main>

        <nav
          className="fixed inset-x-0 bottom-0 z-20 flex border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
          aria-label={t('nav:bottomNav')}
        >
          {BOTTOM_NAV.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10.5px] transition-colors',
                  isActive ? 'font-semibold text-accent' : 'text-muted',
                )
              }
            >
              <entry.icon className="h-[18px] w-[18px]" aria-hidden />
              <span className="max-w-full truncate px-1">{t(entry.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

/** Проверка прав для условного отображения элементов интерфейса. */
export function useCan() {
  const { workspace } = useAuth();
  return (permission: Parameters<typeof can>[1]) => can(workspace?.role ?? null, permission);
}
